import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { faAdd, faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

const INITIAL_VISIBLE_TERMS = 12;

export interface ITaxonomyDropdownOption {
  key: string;
  name: string;
  facet: TaxonomyFacet;
}

export interface ITaxonomyTermDropdownOption {
  id: string;
  key: string;
  name: string;
  facet?: TaxonomyFacet;
  taxonomyKey?: string;
}

export function TaxonomyTermTwoStepDropdown({
  taxonomies,
  terms,
  onSelectTerm,
  buttonLabel,
  startAtTermsForFacet,
}: {
  taxonomies: ITaxonomyDropdownOption[];
  terms: ITaxonomyTermDropdownOption[];
  onSelectTerm: (termId: string) => void;
  buttonLabel?: string;
  startAtTermsForFacet?: TaxonomyFacet;
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedTaxonomyKey, setSelectedTaxonomyKey] = React.useState<string | undefined>(undefined);
  const [termSearch, setTermSearch] = React.useState('');
  const [visibleTerms, setVisibleTerms] = React.useState(INITIAL_VISIBLE_TERMS);

  const facetLockedMode = !!startAtTermsForFacet;

  const selectedTaxonomy = React.useMemo(
    () => taxonomies.find((taxonomy) => taxonomy.key === selectedTaxonomyKey),
    [selectedTaxonomyKey, taxonomies]
  );

  const termsForSelectedTaxonomy = React.useMemo(() => {
    if (facetLockedMode && startAtTermsForFacet) {
      return terms.filter((term) => term.facet === startAtTermsForFacet);
    }

    if (!selectedTaxonomyKey) {
      return [];
    }

    return terms.filter((term) => term.taxonomyKey === selectedTaxonomyKey);
  }, [facetLockedMode, selectedTaxonomyKey, startAtTermsForFacet, terms]);

  const showTaxonomyStep = !facetLockedMode && !selectedTaxonomy;

  const filteredTerms = React.useMemo(() => {
    const needle = termSearch.trim().toLowerCase();
    if (!needle) {
      return termsForSelectedTaxonomy;
    }

    return termsForSelectedTaxonomy.filter((term) => term.name.toLowerCase().includes(needle));
  }, [termSearch, termsForSelectedTaxonomy]);

  const visibleFilteredTerms = filteredTerms.slice(0, visibleTerms);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center text-sm text-primary hover:text-primary/80 font-bold px-2"
        onClick={() => setOpen((current) => !current)}
      >
        <FontAwesomeIcon icon={faAdd} className="text-xs mr-1" /> {buttonLabel || 'Add Filter'}
      </button>

      {open ? (
        <div className="absolute z-20 mt-2 w-[340px] rounded-xl bg-white p-3 shadow-card-hover ring-1 ring-slate-200/80 motion-safe:animate-bp-slide-down">
          {showTaxonomyStep ? (
            <div>
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500 mb-2">
                Select Taxonomy
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {taxonomies.map((taxonomy) => (
                  <button
                    key={taxonomy.key}
                    type="button"
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 text-slate-700"
                    onClick={() => {
                      setSelectedTaxonomyKey(taxonomy.key);
                      setTermSearch('');
                      setVisibleTerms(INITIAL_VISIBLE_TERMS);
                    }}
                  >
                    {taxonomy.name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                {!facetLockedMode ? (
                  <button
                    type="button"
                    className="text-xs font-semibold text-primary hover:underline"
                    onClick={() => {
                      setSelectedTaxonomyKey(undefined);
                      setTermSearch('');
                      setVisibleTerms(INITIAL_VISIBLE_TERMS);
                    }}
                  >
                    Change taxonomy
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">Select Term</span>
                )}
                {!facetLockedMode ? <span className="text-xs text-slate-500">{selectedTaxonomy?.name}</span> : null}
              </div>

              <div className="relative mb-2">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-slate-400 text-sm" />
                </span>
                <input
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-200 rounded bg-white focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Search terms"
                  value={termSearch}
                  onChange={(event) => {
                    setTermSearch(event.target.value);
                    setVisibleTerms(INITIAL_VISIBLE_TERMS);
                  }}
                />
              </div>

              <div className="max-h-64 overflow-y-auto space-y-1">
                {visibleFilteredTerms.map((term) => (
                  <button
                    key={term.id}
                    type="button"
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 text-slate-700"
                    onClick={() => {
                      onSelectTerm(term.id);
                      setOpen(false);
                      setSelectedTaxonomyKey(undefined);
                      setTermSearch('');
                      setVisibleTerms(INITIAL_VISIBLE_TERMS);
                    }}
                  >
                    {term.name}
                  </button>
                ))}
              </div>

              {filteredTerms.length > visibleTerms ? (
                <button
                  type="button"
                  className="mt-2 w-full text-sm font-semibold text-primary hover:underline"
                  onClick={() => setVisibleTerms((value) => value + INITIAL_VISIBLE_TERMS)}
                >
                  Load more
                </button>
              ) : null}
            </div>
          )}

          <button
            type="button"
            className="mt-3 inline-flex items-center text-xs text-slate-500 hover:text-slate-700"
            onClick={() => {
              setOpen(false);
              setSelectedTaxonomyKey(undefined);
              setTermSearch('');
              setVisibleTerms(INITIAL_VISIBLE_TERMS);
            }}
          >
            Close <FontAwesomeIcon icon={faChevronDown} className="text-[10px] ml-1 rotate-180" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
